// PlantPreStartChecks.tsx
// WHS Priority 14 — Plant Pre-Start Inspection Records
// Daily checklist completed by the operator before each shift.
// A failed check automatically creates a defect record in the Plant register
// and blocks allocation of the asset until the defect is closed.
//
// WHS Regulation 2011 (Qld) Part 5.1 — Management of risks of plant
// Plant must not be used unless the operator has inspected it and
// confirmed it is fit for purpose for that shift.

import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { buildDocumentHeader, buildDocumentFooter } from '../utils/documentBranding'
import {
  MOCK_EQUIPMENT, addEquipmentDefect, getOpenEquipmentDefects,
} from '../utils/mockDataExtended'
import {
  CheckCircle, AlertTriangle, X, Plus, Download, Search,
  Truck, Wrench, ChevronRight, Clock, AlertOctagon, ClipboardList,
} from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────────────

type CheckResult = 'PASS' | 'FAIL' | 'NA'

interface CheckItem {
  id:       string
  label:    string
  group:    string
  result:   CheckResult
  note:     string
}

interface PreStartRecord {
  id:           string
  recordNumber: string
  assetId:      string
  assetNumber:  string
  assetName:    string
  jobNumber:    string
  operatorName: string
  conductedAt:  string
  odometer:     number | null
  hoursReading: number | null
  checks:       CheckItem[]
  overallResult: 'PASS' | 'FAIL'
  defectCreated: boolean
  defectId:      string | null
  defectDesc:    string
  notes:         string
}

// ── Checklist templates by asset category ─────────────────────────────────

const VEHICLE_CHECKS: Omit<CheckItem, 'result' | 'note'>[] = [
  { id: 'v1',  group: 'Fluids',          label: 'Engine oil level — within acceptable range' },
  { id: 'v2',  group: 'Fluids',          label: 'Coolant level — within acceptable range' },
  { id: 'v3',  group: 'Fluids',          label: 'Fuel level — sufficient for shift' },
  { id: 'v4',  group: 'Fluids',          label: 'Windscreen washer fluid — sufficient' },
  { id: 'v5',  group: 'Safety devices',  label: 'Service brakes — function and feel normal' },
  { id: 'v6',  group: 'Safety devices',  label: 'Park brake — holds vehicle stationary' },
  { id: 'v7',  group: 'Safety devices',  label: 'Seatbelts (all positions) — present and functional' },
  { id: 'v8',  group: 'Safety devices',  label: 'Headlights, tail lights, indicators — all functional' },
  { id: 'v9',  group: 'Safety devices',  label: 'Reversing beeper — audible and functional' },
  { id: 'v10', group: 'Safety devices',  label: 'Mirrors — all present, clean, adjusted' },
  { id: 'v11', group: 'Safety devices',  label: 'Amber beacon (if fitted) — functional' },
  { id: 'v12', group: 'Safety devices',  label: 'Fire extinguisher — present, charged, accessible' },
  { id: 'v13', group: 'Tyres / wheels',  label: 'All tyres — visually acceptable pressure and condition' },
  { id: 'v14', group: 'Tyres / wheels',  label: 'Wheel nuts — no visible looseness or missing nuts' },
  { id: 'v15', group: 'Body / structure','label': 'No new visible body damage, cracks, or leaks' },
  { id: 'v16', group: 'Body / structure','label': 'Towbar / coupling (if towing) — secure and rated' },
]

const PLANT_CHECKS: Omit<CheckItem, 'result' | 'note'>[] = [
  { id: 'p1',  group: 'Fluids',          label: 'Engine oil level — within acceptable range' },
  { id: 'p2',  group: 'Fluids',          label: 'Hydraulic oil level — within acceptable range' },
  { id: 'p3',  group: 'Fluids',          label: 'Fuel level — sufficient for shift' },
  { id: 'p4',  group: 'Fluids',          label: 'Coolant level — within acceptable range (if water-cooled)' },
  { id: 'p5',  group: 'Safety devices',  label: 'ROPS / FOPS — structurally intact, no cracks or damage' },
  { id: 'p6',  group: 'Safety devices',  label: 'Seatbelt (if applicable) — present and functional' },
  { id: 'p7',  group: 'Safety devices',  label: 'Fire extinguisher — present, charged, accessible' },
  { id: 'p8',  group: 'Safety devices',  label: 'Lights and warning devices — functional' },
  { id: 'p9',  group: 'Controls',        label: 'All controls — function correctly, no stiff or loose controls' },
  { id: 'p10', group: 'Controls',        label: 'Emergency stop — tested and functional' },
  { id: 'p11', group: 'Structure',       label: 'No visible structural damage, cracks, or fluid leaks' },
  { id: 'p12', group: 'Structure',       label: 'Tracks or tyres — acceptable condition' },
  { id: 'p13', group: 'Attachments',     label: 'Attachment(s) — correctly pinned and secured for task' },
  { id: 'p14', group: 'Attachments',     label: 'SWL plates — present and legible' },
]

const EWP_CHECKS: Omit<CheckItem, 'result' | 'note'>[] = [
  ...PLANT_CHECKS.slice(0, 4),
  { id: 'e5',  group: 'Safety devices',   label: 'ROPS / guardrails on platform — intact and secure' },
  { id: 'e6',  group: 'Safety devices',   label: 'Fall arrest anchor point — present and rated' },
  { id: 'e7',  group: 'Safety devices',   label: 'Outriggers — deploy correctly, level indicators functional' },
  { id: 'e8',  group: 'Safety devices',   label: 'Emergency descent — tested and functional' },
  { id: 'e9',  group: 'Controls',         label: 'Ground controls — all functional (override tested)' },
  { id: 'e10', group: 'Controls',         label: 'Platform controls — all functional, proportional' },
  { id: 'e11', group: 'Controls',         label: 'Emergency stop — tested at ground and platform' },
  { id: 'e12', group: 'Structure',        label: 'Boom, jib, and platform — no visible damage or cracks' },
  { id: 'e13', group: 'Structure',        label: 'Chassis — no visible structural damage or fluid leaks' },
  { id: 'e14', group: 'Attachments',      label: 'SWL and rated capacity plates — present and legible' },
]

const TEST_EQUIPMENT_CHECKS: Omit<CheckItem, 'result' | 'note'>[] = [
  { id: 't1', group: 'Calibration',  label: 'Calibration certificate — current (within due date)' },
  { id: 't2', group: 'Condition',    label: 'No visible damage to housing, leads, or probes' },
  { id: 't3', group: 'Function',     label: 'Self-test or zeroing function — passes' },
  { id: 't4', group: 'Function',     label: 'Battery level — sufficient for shift' },
  { id: 't5', group: 'Accessories',  label: 'All leads, probes, and accessories present and undamaged' },
]

const TRAFFIC_CONTROL_CHECKS: Omit<CheckItem, 'result' | 'note'>[] = [
  { id: 'tc1', group: 'Signs',       label: 'All required signs — present, correct, retroreflective sheeting intact' },
  { id: 'tc2', group: 'Signs',       label: 'Sign faces — clean, no damage, legend legible' },
  { id: 'tc3', group: 'Delineation', label: 'Cones / delineators — correct quantity, retro banding intact' },
  { id: 'tc4', group: 'Barriers',    label: 'Barriers — no structural damage, water-filled if ballasted' },
  { id: 'tc5', group: 'Electronic',  label: 'VMS / arrow board (if present) — powers on, display correct' },
  { id: 'tc6', group: 'Storage',     label: 'All items — correctly loaded and secured for transport' },
]

function getChecklistTemplate(category: string): Omit<CheckItem, 'result' | 'note'>[] {
  if (category === 'TEST_EQUIPMENT') return TEST_EQUIPMENT_CHECKS
  if (category === 'TRAFFIC_CONTROL') return TRAFFIC_CONTROL_CHECKS
  if (category === 'PLANT') return PLANT_CHECKS   // overridden for EWP below
  return VEHICLE_CHECKS
}

function getChecklistForAsset(asset: typeof MOCK_EQUIPMENT[0]): CheckItem[] {
  const isEWP = asset.name.toLowerCase().includes('ewp') || asset.name.toLowerCase().includes('snorkel') || asset.name.toLowerCase().includes('boom lift')
  const template = isEWP ? EWP_CHECKS : getChecklistTemplate(asset.category)
  return template.map(c => ({ ...c, result: 'PASS' as CheckResult, note: '' }))
}

// ── Helpers ────────────────────────────────────────────────────────────────

const fmt   = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'
const fmtDT = (s?: string | null) =>
  s ? new Date(s).toLocaleString('en-AU',  { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
const NOW = new Date().toISOString()
const dAgo = (h: number) => new Date(Date.now() - h * 3600000).toISOString()

// ── Seed records ──────────────────────────────────────────────────────────

const seedChecks = (template: Omit<CheckItem, 'result' | 'note'>[], failIds: string[] = []): CheckItem[] =>
  template.map(c => ({
    ...c,
    result: failIds.includes(c.id) ? ('FAIL' as CheckResult) : ('PASS' as CheckResult),
    note: failIds.includes(c.id) ? 'Defect noted — see pre-start record' : '',
  }))

const SEED_RECORDS: PreStartRecord[] = [
  {
    id: 'ps-001', recordNumber: 'PS-VEH001-20260808',
    assetId: 'eq1', assetNumber: 'VEH-001', assetName: 'Ford Ranger XL 2023',
    jobNumber: 'TP-2026-001', operatorName: 'Tony Rivera',
    conductedAt: dAgo(10),
    odometer: 68520, hoursReading: null,
    checks: seedChecks(VEHICLE_CHECKS),
    overallResult: 'PASS', defectCreated: false, defectId: null, defectDesc: '',
    notes: 'Vehicle in good order. Odometer 68,520 km.',
  },
  {
    id: 'ps-002', recordNumber: 'PS-PLT001-20260808',
    assetId: 'eq5', assetNumber: 'PLT-001', assetName: 'Kubota KX016-4 Excavator',
    jobNumber: 'TP-2026-004', operatorName: 'Ben Walsh',
    conductedAt: dAgo(9),
    odometer: null, hoursReading: 1248,
    checks: seedChecks(PLANT_CHECKS, ['p2']),
    overallResult: 'FAIL', defectCreated: true, defectId: 'def-ps-002',
    defectDesc: 'Hydraulic oil level below minimum on dipstick. Topped up — recheck before next shift. Possible slow leak at boom cylinder.',
    notes: 'Asset taken out of service pending leak inspection.',
  },
  {
    id: 'ps-003', recordNumber: 'PS-VEH002-20260807',
    assetId: 'eq2', assetNumber: 'VEH-002', assetName: 'Toyota Hilux SR 2022',
    jobNumber: 'TP-2026-002', operatorName: 'Mike Torres',
    conductedAt: dAgo(22),
    odometer: 79950, hoursReading: null,
    checks: seedChecks(VEHICLE_CHECKS),
    overallResult: 'PASS', defectCreated: false, defectId: null, defectDesc: '',
    notes: '',
  },
]

// ── PDF generation ─────────────────────────────────────────────────────────

function generatePreStartPDF(record: PreStartRecord) {
  const failCount = record.checks.filter(c => c.result === 'FAIL').length
  const naCount   = record.checks.filter(c => c.result === 'NA').length
  const groups    = [...new Set(record.checks.map(c => c.group))]

  const checksHtml = groups.map(group => {
    const items = record.checks.filter(c => c.group === group)
    return `
<div style="margin-bottom:14px">
  <div style="font-weight:bold;font-size:10pt;color:#1F3864;border-left:3px solid #2E75B6;padding-left:6px;margin-bottom:6px">${group}</div>
  <table style="width:100%;border-collapse:collapse;font-size:9pt">
    ${items.map(c => `
    <tr style="border-bottom:1px solid #eee">
      <td style="padding:4px 8px;width:60%">${c.label}</td>
      <td style="padding:4px 8px;width:12%;text-align:center;font-weight:bold;color:${c.result==='FAIL'?'#C00000':c.result==='NA'?'#888':'#375623'}">${c.result}</td>
      <td style="padding:4px 8px;width:28%;font-size:8.5pt;color:#666">${c.note || ''}</td>
    </tr>`).join('')}
  </table>
</div>`
  }).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Plant Pre-Start — ${record.recordNumber}</title>
<style>
*{box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:18mm 20mm}
.doc-title{border-bottom:3px solid #1F3864;padding-bottom:8px;margin-bottom:14px}
h2{font-size:14pt;font-weight:900;color:#1F3864;margin:0 0 2px;text-transform:uppercase}
.subtitle{font-size:9pt;color:#555}
.meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #ccc;margin-bottom:14px}
.mc{padding:5px 10px;border-bottom:1px solid #eee;font-size:9pt}
.mc:nth-child(odd){background:#f7f8fa;font-weight:bold;color:#555}
.result-box{padding:10px 14px;border-radius:4px;margin-bottom:14px;font-size:11pt;font-weight:bold;text-align:center}
.result-pass{background:#E8F5E9;border:2px solid #4CAF50;color:#1B5E20}
.result-fail{background:#FFEBEE;border:2px solid #F44336;color:#B71C1C}
.defect-box{background:#FFF3E0;border:1.5px solid #FB8C00;border-radius:4px;padding:10px 14px;margin-bottom:14px;font-size:9.5pt;color:#E65100}
.sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:16px}
.sig-box{border:1.5px solid #999;border-radius:4px;padding:10px 14px;min-height:60px}
.sig-lbl{font-size:8pt;font-weight:bold;color:#555;text-transform:uppercase;margin-bottom:4px}
.sig-line{border-bottom:1px solid #555;height:22px;margin:6px 0 4px}
.sig-name{font-size:8pt;color:#777}
@media print{body{margin:14mm}}
</style></head><body>
${buildDocumentHeader('INVOICE')}
<div class="doc-title">
  <h2>Plant Pre-Start Inspection Record</h2>
  <div class="subtitle">WHS Regulation 2011 (Qld) Part 5.1 — Completed before first use each shift</div>
</div>

<div class="meta-grid">
  <div class="mc">Record number</div>  <div class="mc">${record.recordNumber}</div>
  <div class="mc">Asset</div>          <div class="mc">${record.assetNumber} — ${record.assetName}</div>
  <div class="mc">Job number</div>     <div class="mc">${record.jobNumber}</div>
  <div class="mc">Operator</div>       <div class="mc">${record.operatorName}</div>
  <div class="mc">Date / time</div>    <div class="mc">${fmtDT(record.conductedAt)}</div>
  ${record.odometer    ? `<div class="mc">Odometer</div><div class="mc">${record.odometer.toLocaleString()} km</div>` : ''}
  ${record.hoursReading ? `<div class="mc">Hours reading</div><div class="mc">${record.hoursReading.toLocaleString()} h</div>` : ''}
</div>

<div class="result-box ${record.overallResult === 'PASS' ? 'result-pass' : 'result-fail'}">
  Overall result: ${record.overallResult}
  ${failCount > 0 ? ` — ${failCount} item${failCount!==1?'s':''} failed` : ''}
  ${naCount   > 0 ? ` · ${naCount} N/A` : ''}
</div>

${record.defectCreated ? `<div class="defect-box">⚠ DEFECT RECORDED: ${record.defectDesc} Asset must not be used until this defect is closed by a supervisor or maintenance officer.</div>` : ''}

${checksHtml}

${record.notes ? `<div style="margin-bottom:14px"><strong style="font-size:9pt">Notes:</strong><div style="background:#f9fafb;border:1px solid #e0e0e0;border-radius:3px;padding:8px 10px;font-size:9pt;margin-top:4px">${record.notes}</div></div>` : ''}

<div class="sig-grid">
  <div class="sig-box">
    <div class="sig-lbl">Operator sign-off</div>
    <div class="sig-line">✓ Signed electronically</div>
    <div class="sig-name">${record.operatorName}</div>
    <div class="sig-name" style="margin-top:4px">Date/time: ${fmtDT(record.conductedAt)}</div>
  </div>
  <div class="sig-box">
    <div class="sig-lbl">Supervisor review (if FAIL)</div>
    <div class="sig-line">${record.defectCreated ? 'Defect recorded — see Plant register' : 'N/A — no defects identified'}</div>
    <div class="sig-name">${record.defectId ? `Defect ref: ${record.defectId}` : ''}</div>
  </div>
</div>

${buildDocumentFooter('INVOICE', record.recordNumber)}
</body></html>`

  const w = window.open('', '_blank', 'width=900,height=780')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html)
  w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

// ── Pre-start form ─────────────────────────────────────────────────────────

function PreStartForm({
  onSave, onCancel, operatorName,
}: { onSave: (r: PreStartRecord) => void; onCancel: () => void; operatorName: string }) {
  const [assetId, setAssetId]     = useState(MOCK_EQUIPMENT[0].id)
  const [jobNum, setJobNum]       = useState('')
  const [odo, setOdo]             = useState<string>('')
  const [hours, setHours]         = useState<string>('')
  const [notes, setNotes]         = useState('')
  const [defectDesc, setDefectDesc] = useState('')

  const asset = MOCK_EQUIPMENT.find(e => e.id === assetId)!
  const [checks, setChecks] = useState<CheckItem[]>(getChecklistForAsset(asset))

  const handleAssetChange = (id: string) => {
    setAssetId(id)
    const a = MOCK_EQUIPMENT.find(e => e.id === id)!
    setChecks(getChecklistForAsset(a))
  }

  const setResult = (id: string, result: CheckResult) =>
    setChecks(prev => prev.map(c => c.id === id ? { ...c, result } : c))
  const setNote   = (id: string, note: string) =>
    setChecks(prev => prev.map(c => c.id === id ? { ...c, note } : c))

  const failItems  = checks.filter(c => c.result === 'FAIL')
  const hasFail    = failItems.length > 0
  const overallResult: 'PASS' | 'FAIL' = hasFail ? 'FAIL' : 'PASS'
  const groups = [...new Set(checks.map(c => c.group))]

  const handleSave = () => {
    const now = new Date().toISOString()
    const dateStr = now.split('T')[0].replace(/-/g, '').slice(2)
    let defectId: string | null = null

    if (hasFail && defectDesc.trim()) {
      const defId = `def-ps-${Date.now()}`
      addEquipmentDefect({
        equipmentId: asset.id,
        assetNumber: asset.assetNumber,
        severity:    failItems.some(f => ['v5','v6','p5','e7','e8','e11'].includes(f.id)) ? 'CRITICAL' : 'MAJOR',
        description: `Pre-start ${dateStr}: ${defectDesc.trim()}`,
        status:      'OPEN',
        reportedAt:  now,
      })
      defectId = defId
    }

    const record: PreStartRecord = {
      id:             `ps-${Date.now()}`,
      recordNumber:   `PS-${asset.assetNumber.replace('-','')}-${dateStr}`,
      assetId:        asset.id,
      assetNumber:    asset.assetNumber,
      assetName:      asset.name,
      jobNumber:      jobNum.trim() || asset.currentJobNumber || 'Yard',
      operatorName,
      conductedAt:    now,
      odometer:       odo   ? Number(odo)   : null,
      hoursReading:   hours ? Number(hours) : null,
      checks,
      overallResult,
      defectCreated:  !!defectId,
      defectId,
      defectDesc:     defectDesc.trim(),
      notes:          notes.trim(),
    }
    onSave(record)
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="bg-[#1F3864] text-white px-5 py-3 flex items-center justify-between">
        <div className="font-semibold text-sm">New Plant Pre-Start Inspection</div>
        <button onClick={onCancel}><X className="w-4 h-4 text-white/70 hover:text-white" /></button>
      </div>

      <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">

        {/* Asset + context */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="col-span-2">
            <label className="label-sm">Asset</label>
            <select value={assetId} onChange={e => handleAssetChange(e.target.value)} className="input text-sm w-full">
              {MOCK_EQUIPMENT.filter(e => e.status !== 'DISPOSED').map(e => (
                <option key={e.id} value={e.id}>{e.assetNumber} — {e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-sm">Job number</label>
            <input value={jobNum} onChange={e => setJobNum(e.target.value)} placeholder={asset.currentJobNumber ?? 'TP-2026-…'} className="input text-sm w-full" />
          </div>
          {asset.category === 'VEHICLE' ? (
            <div>
              <label className="label-sm">Odometer (km)</label>
              <input type="number" value={odo} onChange={e => setOdo(e.target.value)} placeholder={String(asset.currentOdometer ?? '')} className="input text-sm w-full" />
            </div>
          ) : (
            <div>
              <label className="label-sm">Hours reading (h)</label>
              <input type="number" value={hours} onChange={e => setHours(e.target.value)} placeholder="0" className="input text-sm w-full" />
            </div>
          )}
        </div>

        {/* Open defects warning */}
        {getOpenEquipmentDefects(assetId).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2">
            <AlertOctagon className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <strong>This asset has {getOpenEquipmentDefects(assetId).length} open defect{getOpenEquipmentDefects(assetId).length !== 1 ? 's' : ''} in the Plant register.</strong>{' '}
              {getOpenEquipmentDefects(assetId).map(d => d.description).join('; ')}
            </div>
          </div>
        )}

        {/* Checklist */}
        <div className="space-y-5">
          {groups.map(group => (
            <div key={group}>
              <div className="text-xs font-bold text-[#1F3864] uppercase tracking-wide border-l-4 border-[#2E75B6] pl-2 mb-3">{group}</div>
              <div className="space-y-1.5">
                {checks.filter(c => c.group === group).map(check => (
                  <div key={check.id} className={`border rounded-lg transition-colors ${check.result === 'FAIL' ? 'border-red-300 bg-red-50/50' : check.result === 'NA' ? 'border-gray-100' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <span className="text-sm text-gray-800 flex-1">{check.label}</span>
                      <div className="flex gap-1 flex-shrink-0">
                        {(['PASS', 'FAIL', 'NA'] as CheckResult[]).map(r => (
                          <button key={r} onClick={() => setResult(check.id, r)}
                            className={`text-xs font-bold px-2.5 py-1 rounded transition-colors ${
                              check.result === r
                                ? r === 'PASS' ? 'bg-green-600 text-white'
                                : r === 'FAIL' ? 'bg-red-600 text-white'
                                : 'bg-gray-400 text-white'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}>{r}</button>
                        ))}
                      </div>
                    </div>
                    {check.result === 'FAIL' && (
                      <div className="px-3 pb-2">
                        <input value={check.note} onChange={e => setNote(check.id, e.target.value)}
                          placeholder="Describe the defect or issue…"
                          className="input text-xs w-full" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Defect summary */}
        {hasFail && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {failItems.length} failed check{failItems.length !== 1 ? 's' : ''} — defect description required
            </div>
            <div className="text-xs text-red-700 space-y-1">
              {failItems.map(f => <div key={f.id}>• {f.label}{f.note ? `: ${f.note}` : ''}</div>)}
            </div>
            <textarea value={defectDesc} onChange={e => setDefectDesc(e.target.value)} rows={3}
              placeholder="Summarise the defect(s) for the Plant register. This will create an OPEN defect record and block allocation of the asset."
              className="input text-sm w-full resize-none" />
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="label-sm">Additional notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="Any additional observations or comments…"
            className="input text-sm w-full resize-none mt-1" />
        </div>
      </div>

      <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between gap-3">
        <div className={`text-sm font-semibold flex items-center gap-1.5 ${hasFail ? 'text-red-700' : 'text-green-700'}`}>
          {hasFail
            ? <><AlertTriangle className="w-4 h-4" />Result: FAIL — {failItems.length} issue{failItems.length !== 1 ? 's' : ''}</>
            : <><CheckCircle className="w-4 h-4" />Result: PASS</>}
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-secondary text-sm">Cancel</button>
          <button onClick={handleSave}
            disabled={hasFail && !defectDesc.trim()}
            className="btn-primary text-sm disabled:opacity-40 flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />Submit pre-start
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export function PlantPreStartChecks() {
  const { isTier, user } = useAuthStore()
  const canCreate = true   // all authenticated users can submit a pre-start
  const canReview = isTier('B_PM') || isTier('B_PC') || isTier('C_WHSO') || isTier('A_DEVELOPER')

  const [records, setRecords]     = useState<PreStartRecord[]>(SEED_RECORDS)
  const [showNew, setShowNew]     = useState(false)
  const [selected, setSelected]   = useState<PreStartRecord | null>(null)
  const [search, setSearch]       = useState('')
  const [filterResult, setFilterResult] = useState<'ALL' | 'PASS' | 'FAIL'>('ALL')

  const filtered = useMemo(() => records.filter(r => {
    const ms = !search || r.assetNumber.toLowerCase().includes(search.toLowerCase())
                       || r.assetName.toLowerCase().includes(search.toLowerCase())
                       || r.jobNumber.toLowerCase().includes(search.toLowerCase())
                       || r.operatorName.toLowerCase().includes(search.toLowerCase())
    const mf = filterResult === 'ALL' || r.overallResult === filterResult
    return ms && mf
  }), [records, search, filterResult])

  const failCount  = records.filter(r => r.overallResult === 'FAIL').length
  const todayCount = records.filter(r => r.conductedAt.split('T')[0] === new Date().toISOString().split('T')[0]).length

  const handleSave = (record: PreStartRecord) => {
    setRecords(prev => [record, ...prev])
    setSelected(record)
    setShowNew(false)
  }

  if (showNew) {
    return (
      <div className="space-y-4">
        <PreStartForm
          operatorName={user?.fullName ?? 'Operator'}
          onSave={handleSave}
          onCancel={() => setShowNew(false)}
        />
      </div>
    )
  }

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#2E75B6]" />
            Plant Pre-Start Inspections
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            WHS Regulation 2011 (Qld) Part 5.1 · Complete before first use of any plant each shift
          </p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" />New pre-start
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total records',    value: records.length,  color: 'text-gray-900',  sub: 'all time' },
          { label: 'Completed today',  value: todayCount,      color: 'text-blue-700',  sub: 'this shift day' },
          { label: 'FAIL results',     value: failCount,       color: failCount > 0 ? 'text-red-600' : 'text-gray-400', sub: 'defects raised' },
          { label: 'Open defects',     value: getOpenEquipmentDefects().length, color: getOpenEquipmentDefects().length > 0 ? 'text-amber-700' : 'text-gray-400', sub: 'in plant register' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {/* Defects raised from pre-starts */}
      {failCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800">
          <AlertOctagon className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span>
            <strong>{failCount} failed pre-start{failCount !== 1 ? 's' : ''} on record.</strong>{' '}
            Open defects have been created in the Plant &amp; Equipment register. Assets cannot be allocated to jobs until defects are closed.
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search asset, job, or operator…" className="input pl-9 text-sm w-full" />
        </div>
        <select value={filterResult} onChange={e => setFilterResult(e.target.value as any)} className="input text-sm w-36">
          <option value="ALL">All results</option>
          <option value="PASS">Pass only</option>
          <option value="FAIL">Fail only</option>
        </select>
      </div>

      {/* List + Detail */}
      <div className="flex gap-5 items-start">
        <div className={`${selected ? 'w-1/2' : 'w-full'} space-y-2 transition-all`}>
          {filtered.map(rec => {
            const isSel  = selected?.id === rec.id
            const isFail = rec.overallResult === 'FAIL'
            return (
              <div key={rec.id}
                onClick={() => setSelected(isSel ? null : rec)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSel ? 'border-[#2E75B6] ring-2 ring-blue-100' : isFail ? 'border-red-300' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{rec.recordNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${isFail ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {rec.overallResult}
                      </span>
                    </div>
                    <div className="font-semibold text-sm text-gray-900">{rec.assetNumber} — {rec.assetName}</div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                      <span>{fmtDT(rec.conductedAt)}</span>
                      <span>{rec.operatorName}</span>
                      <span>Job: {rec.jobNumber}</span>
                      {rec.odometer    && <span>Odo: {rec.odometer.toLocaleString()} km</span>}
                      {rec.hoursReading && <span>Hours: {rec.hoursReading.toLocaleString()} h</span>}
                      {isFail && <span className="text-red-600 font-medium">{rec.checks.filter(c => c.result === 'FAIL').length} fail item{rec.checks.filter(c => c.result === 'FAIL').length !== 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSel ? 'text-[#2E75B6]' : 'text-gray-400'}`} />
                </div>
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">
              No pre-start records match the current filter
            </div>
          )}
        </div>

        {selected && (
          <div className="w-1/2 sticky top-6 space-y-3" style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 border-b bg-gray-50 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-mono text-gray-400">{selected.recordNumber}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${selected.overallResult === 'FAIL' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {selected.overallResult}
                    </span>
                  </div>
                  <div className="font-semibold text-sm text-gray-900">{selected.assetNumber} — {selected.assetName}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{fmtDT(selected.conductedAt)} · {selected.operatorName}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => generatePreStartPDF(selected)} className="btn-secondary text-xs flex items-center gap-1">
                    <Download className="w-3.5 h-3.5" />PDF
                  </button>
                  <button onClick={() => setSelected(null)}><X className="w-4 h-4 text-gray-400" /></button>
                </div>
              </div>

              {/* Defect alert */}
              {selected.defectCreated && (
                <div className="mx-4 mt-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800">
                  <AlertTriangle className="w-4 h-4 inline mr-1.5 text-red-600" />
                  <strong>Defect recorded in Plant register.</strong> {selected.defectDesc}
                  <div className="text-xs text-red-600 mt-1">Asset blocked from allocation until defect is closed by a supervisor or maintenance officer.</div>
                </div>
              )}

              {/* Check results */}
              <div className="p-4 space-y-4">
                {[...new Set(selected.checks.map(c => c.group))].map(group => (
                  <div key={group}>
                    <div className="text-xs font-bold text-[#1F3864] uppercase tracking-wide border-l-4 border-[#2E75B6] pl-2 mb-2">{group}</div>
                    <div className="space-y-1">
                      {selected.checks.filter(c => c.group === group).map(check => (
                        <div key={check.id} className={`flex items-start gap-2 px-3 py-2 rounded-lg ${check.result === 'FAIL' ? 'bg-red-50' : check.result === 'NA' ? 'bg-gray-50' : ''}`}>
                          <span className={`text-xs font-bold mt-0.5 w-10 flex-shrink-0 ${check.result === 'FAIL' ? 'text-red-600' : check.result === 'NA' ? 'text-gray-400' : 'text-green-600'}`}>
                            {check.result}
                          </span>
                          <div className="text-xs text-gray-700 flex-1">
                            {check.label}
                            {check.note && <div className="text-red-600 mt-0.5 italic">{check.note}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {selected.notes && (
                  <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3">
                    <strong>Notes:</strong> {selected.notes}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
